import { StrictMode } from 'react';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import App from './App.tsx';
import type { Config } from './config.schema';
import { setupAPIM } from './interceptors.ts';
import './index.css';
import { useConfigStore, type AppConfig } from './store/ConfigStore';
import { applyClientConfig, getConfig } from './utils/appConfig';
import type { CarlDevTools } from './utils/dev';

export type InitProps = {
  apiKey?: string;
  portals?: AppConfig['portals'];
  config?: Config;
  allConfig?: Config[];
};

declare global {
  interface Window {
    carl: {
      init: (props: InitProps) => void;
      dev?: CarlDevTools;
    };
  }
}

window.carl = { init };

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    tracesSampleRate: import.meta.env.TEST ? 0 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

async function init(props: InitProps = {}) {
  const { apiKey, portals, allConfig = [], config = getConfig(allConfig) } = props;

  applyClientConfig(config);
  setupAPIM(apiKey ?? import.meta.env.VITE_APIM_KEY);

  useConfigStore.setState({ portals, config });

  const root = portals ? document.createElement('div') : document.querySelector(String(config?.rootElement ?? '#root'));

  if (!root) {
    console.error('CARL init error: Target root element not found.', config);
    return;
  }

  const appRoot = createRoot(root);
  appRoot.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

import('./utils/dev').then(({ initDevTools }) => {
  initDevTools();
});
